import { useState } from 'react';
import { useIntl } from 'react-intl';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Folder } from '~/eds/src/components/Folder/Folder';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Info } from '~/eds/src/modules/Info/Info';
import { Table } from '~/eds/src/modules/Table/Table';

import { ConfigurationPanelProps, FileEvent } from './types';
import { useConfigurationInfos } from './useConfigurationInfos';

const STORAGE_ERROR_PAGE_SIZE = 1000;

export const ConfigurationDetailPanel = ({
  config,
  errors,
  isLoading,
  isError,
  tab,
  onHide,
}: ConfigurationPanelProps) => {
  const intl = useIntl();

  const [width, setWidth] = useState('m');

  const {
    id,
    allowedSyncFileTypes,
    createdBy,
    dateCreated,
    dateUpdated,
    name,
    evisortFolderPath,
    provider,
    providerOptions,
    skipEmptyFolders,
    providerFolderPath,
    providerPrefix,
  } = config;

  type PaginationParams = {
    pageIndex: number;
  };

  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState(tab);

  const columns = [
    {
      title: intl.formatMessage(
        {
          id: 'StorageConfigurationDetailPanel.fileName',
          defaultMessage: '{providerName} File Name',
          description: 'Column title for provider file name',
        },
        { providerName: name },
      ),
      key: 'filename',
      cellType: 'text',
      disableSortBy: true,
      mapCellProps: (data: FileEvent) => ({
        text: data.filename,
      }),
    },
    {
      title: intl.formatMessage({
        id: 'StorageConfigurationDetailPanel.toPath',
        defaultMessage: 'Destination Path',
        description: 'Column title for provider destination path',
      }),
      key: 'toPath',
      disableSortBy: true,
      renderCell: ({ toPath, provider }: FileEvent) => (
        <Folder name={provider} path={toPath ? toPath : ''} isConcise />
      ),
    },
    {
      title: intl.formatMessage({
        id: 'StorageConfigurationDetailPanel.fromPath',
        defaultMessage: 'Source path',
        description: 'Column title for provider source path',
      }),
      key: 'fromPath',
      disableSortBy: true,
      renderCell: ({ fromPath, provider }: FileEvent) => (
        <Folder name={provider} path={fromPath ? fromPath : ''} isConcise />
      ),
    },
    {
      title: intl.formatMessage({
        id: 'StorageConfigurationDetailPanel.direction',
        defaultMessage: 'Direction',
        description: 'Column title for provider direction',
      }),
      key: 'direction',
      cellType: 'text',
      disableSortBy: true,
      mapCellProps: (data: FileEvent) => ({
        text:
          data.direction === 'PROVIDER_TO_EVIFS'
            ? `${name} → Evisort`
            : `Evisort → ${name}`,
      }),
    },
    {
      title: intl.formatMessage({
        id: 'StorageConfigurationDetailPanel.operatorType',
        defaultMessage: 'Operator Type',
        description: 'Column title for provider operator type',
      }),
      key: 'operationType',
      cellType: 'text',
      disableSortBy: true,
      mapCellProps: (data: FileEvent) => ({
        text: data.operationType,
      }),
    },
    {
      title: intl.formatMessage({
        id: 'StorageConfigurationDetailPanel.timestamp',
        defaultMessage: 'Date',
        description: 'Column title for timestamp',
      }),
      width: 'l',
      key: 'timestamp',
      cellType: 'datetime',
      disableSortBy: true,
      mapCellProps: (data: FileEvent) => ({
        datetime: data.timestamp,
        format: 'short_datetime',
      }),
    },
    {
      title: intl.formatMessage({
        id: 'StorageConfigurationDetailPanel.errorDetails',
        defaultMessage: 'Error Details',
        description: 'Column title for error details',
      }),
      key: 'errorDetails',
      cellType: 'text',
      disableSortBy: true,
      mapCellProps: (data: FileEvent) => ({
        text: data.errorDetails,
      }),
    },
  ];

  const placeholderContent = isError
    ? {
        image: 'empty-no-groups',
        message: intl.formatMessage({
          id: 'StorageConfigurationDetailPanel.errorMessage',
          defaultMessage:
            'Something went wrong when retrieving the errors. Please refresh and load again or contact your administrator.',
          description: 'Message for error state',
        }),
      }
    : errors?.count === 0
      ? {
          image: 'empty-no-groups',
          title: intl.formatMessage({
            id: 'StorageConfigurationDetailPanel.noErrorsTitle',
            defaultMessage: 'No Errors Found',
            description: 'Title for empty state when no errors exist.',
          }),
          message: intl.formatMessage(
            {
              id: 'StorageConfigurationDetailPanel.noErrorsMessage',
              defaultMessage:
                'No errors available for sync pair Storage {providerFolderPath} : {evisortFolderName}.',
              description: 'Message for empty state when no errors exist',
            },
            {
              providerFolderPath,
              evisortFolderName: evisortFolderPath,
            },
          ),
        }
      : undefined;

  const tabs = {
    selectedTab: activeTab,
    onSelectTab: setActiveTab,
    tabs: [
      {
        label: intl.formatMessage({
          id: 'StorageConfigurationDetailPanel.configurationTab',
          defaultMessage: 'Configuration',
          description: 'Label for configuration tab',
        }),
        value: 'config',
        panel: (
          <Info
            sections={[
              {
                title: '',
                infos: useConfigurationInfos({
                  id,
                  providerOptions,
                  name,
                  createdBy,
                  evisortFolderPath,
                  allowedSyncFileTypes,
                  dateCreated,
                  dateUpdated,
                  provider,
                  skipEmptyFolders,
                  providerFolderPath,
                  providerPrefix,
                }),
              },
            ]}
          />
        ),
      },
      {
        label: intl.formatMessage({
          id: 'StorageConfigurationDetailPanel.errorLogTab',
          defaultMessage: 'Error Log',
          description: 'Label for error log tab',
        }),
        value: 'errors',
        count: errors?.count,
        panel: (
          <ContentContainer
            placeholderContent={placeholderContent}
            loadingContent={{
              isLoading,
              message: intl.formatMessage({
                id: 'StorageConfigurationDetailPanel.loadingErrors',
                defaultMessage: 'Loading...',
                description: 'Message displayed while loading error logs',
              }),
            }}
          >
            <Table
              name="errors"
              columns={columns}
              data={errors?.results.slice(
                (page - 1) * STORAGE_ERROR_PAGE_SIZE,
                page * STORAGE_ERROR_PAGE_SIZE,
              )}
              totalCount={errors?.count}
              options={{
                enableExportXlsx: true,
                enableManageColumns: false,
                enableSelectRows: false,
              }}
              state={{
                pageIndex: page,
                pageSize: STORAGE_ERROR_PAGE_SIZE,
                sortBy: [
                  {
                    id: 'timestamp',
                    desc: true,
                  },
                ],
              }}
              onPaginate={({ pageIndex }: PaginationParams) => {
                setPage(pageIndex);
              }}
            />
          </ContentContainer>
        ),
      },
    ],
  };

  return (
    <Panel
      hidden={{
        placement: 'left',
        onHide,
      }}
      position="right"
      title={intl.formatMessage(
        {
          id: 'StorageConfigurationDetailPanel.panelTitle',
          defaultMessage: 'Storage folder name: {folderPath}',
          description:
            'Title for configuration panel. Example: folderPath = "/contracts/drafts"',
        },
        { folderPath: evisortFolderPath },
      )}
      width={width}
      actions={[
        {
          text:
            width === 'm'
              ? intl.formatMessage({
                  id: 'StorageConfigurationDetailPanel.expandAction',
                  defaultMessage: 'Expand',
                  description: 'Action button text to expand the panel',
                })
              : intl.formatMessage({
                  id: 'StorageConfigurationDetailPanel.condenseAction',
                  defaultMessage: 'Condense',
                  description: 'Action button text to condense the panel',
                }),
          onClick: () => {
            setWidth(width === 'm' ? 'calc(100vw - 46px)' : 'm');
          },
          icon: width === 'm' ? 'maximize' : 'minimize',
        },
      ]}
      tabs={tabs}
    ></Panel>
  );
};
